import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  List,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { getTiposContratosNominas } from 'api/catalogos/tiposContratosNominas';
import { getTiposRegimenesNominas } from 'api/catalogos/tiposRegimenesNominas';
import { getTiposJornadasNominas } from 'api/catalogos/tiposJornadasNominas';
import { CAMPOS_CORREGIBLES_RECIBO } from 'api/nominas/timbrado';

const { Text } = Typography;

// Atributo del XML que señala la validación del servidor -> campo del recibo que lo alimenta
export const CAMPO_POR_ATRIBUTO = Object.freeze({
  Curp: 'curp',
  Rfc: 'rfc',
  NumEmpleado: 'numero_de_empleado',
  NumSeguridadSocial: 'numero_de_seguridad_social',
  DomicilioFiscalReceptor: 'codigo_postal_fiscal_texto_libre',
  ClaveEntFed: 'clave_entidad_federativa',
  RegistroPatronal: 'registro_patronal',
  TipoContrato: 'tipo_de_contrato_sat',
  TipoRegimen: 'tipo_regimen_nomina',
  TipoJornada: 'tipo_de_jornada_sat',
});

// Problemas que no se corrigen en el recibo, con la explicación que se muestra
const EXPLICACION_POR_ATRIBUTO = Object.freeze({
  PeriodicidadPago: 'Sale de la periodicidad del periodo de la nómina, no del recibo.',
});
export const EXPLICACION_NO_CORREGIBLE = 'No se corrige en el recibo: proviene del cálculo de la '
  + 'nómina (importes y cuadres) o de su configuración.';

const ETIQUETAS = Object.freeze({
  curp: 'CURP',
  rfc: 'RFC',
  numero_de_empleado: 'Número de Empleado',
  numero_de_seguridad_social: 'Número de Seguridad Social',
  codigo_postal_fiscal_texto_libre: 'Código Postal Fiscal',
  clave_entidad_federativa: 'Clave de Entidad Federativa',
  registro_patronal: 'Registro Patronal',
  tipo_de_contrato_sat: 'Tipo de Contrato SAT',
  tipo_regimen_nomina: 'Tipo de Régimen',
  tipo_de_jornada_sat: 'Tipo de Jornada SAT',
});

const LONGITUD_MAXIMA = Object.freeze({
  curp: 18,
  rfc: 13,
  numero_de_empleado: 100,
  numero_de_seguridad_social: 11,
  codigo_postal_fiscal_texto_libre: 5,
  clave_entidad_federativa: 50,
  registro_patronal: 11,
});

// Campos que se eligen de un catálogo del SAT (el resto es texto)
const CAMPOS_CATALOGO = ['tipo_de_contrato_sat', 'tipo_regimen_nomina', 'tipo_de_jornada_sat'];

const PATRON_ATRIBUTO = /@(\w+)|attribute '(\w+)'/;

/**
 * Campo del recibo que corrige un problema de la validación, o null si no se corrige aquí.
 * @param {{elemento: string, mensaje: string}} problema
 * @returns {string|null}
 */
export const campoDelProblema = (problema) => {
  const texto = `${problema?.elemento || ''} ${problema?.mensaje || ''}`;
  const coincidencia = texto.match(PATRON_ATRIBUTO);
  const atributo = coincidencia && (coincidencia[1] || coincidencia[2]);
  return (atributo && CAMPO_POR_ATRIBUTO[atributo]) || null;
};

const explicacionDelProblema = (problema) => {
  const texto = `${problema?.elemento || ''} ${problema?.mensaje || ''}`;
  const coincidencia = texto.match(PATRON_ATRIBUTO);
  const atributo = coincidencia && (coincidencia[1] || coincidencia[2]);
  return (atributo && EXPLICACION_POR_ATRIBUTO[atributo]) || EXPLICACION_NO_CORREGIBLE;
};

const vacioANulo = (valor) => (valor === '' || valor === undefined ? null : valor);

const opcionesCatalogo = (catalogo) => (catalogo || []).map((registro) => ({
  value: registro.id,
  label: [registro.clave, registro.descripcion].filter(Boolean).join(' - '),
}));

/**
 * Panel para revisar los problemas de validación de un recibo Sin Timbrar o Con Error,
 * corregir los datos que se guardan en el propio recibo y volver a validarlo. El guardado y la
 * revalidación los hace la vista (onGuardar); el servidor aplica todas sus protecciones.
 */
const CorreccionDatosRecibo = ({
  visible,
  recibo,
  validacion,
  validando,
  soloLectura,
  motivoSoloLectura,
  seleccionado,
  onGuardar,
  onRevalidar,
  onSeleccionar,
  onVerDatosTimbrado,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [catalogos, setCatalogos] = useState(null);

  useEffect(() => {
    if (!visible || !recibo) return;
    form.setFieldsValue(CAMPOS_CORREGIBLES_RECIBO.reduce((valores, campo) => ({
      ...valores, [campo]: recibo[campo] ?? null,
    }), {}));
  }, [visible, recibo, form]);

  useEffect(() => {
    let mounted = true;
    if (!visible || catalogos) return undefined;
    const fetchAll = async () => {
      const [contratos, regimenes, jornadas] = await Promise.all([
        getTiposContratosNominas(),
        getTiposRegimenesNominas(),
        getTiposJornadasNominas(),
      ]);
      if (mounted) {
        setCatalogos({
          tipo_de_contrato_sat: opcionesCatalogo(contratos),
          tipo_regimen_nomina: opcionesCatalogo(regimenes),
          tipo_de_jornada_sat: opcionesCatalogo(jornadas),
        });
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [visible, catalogos]);

  const problemas = validacion?.problemas || [];
  const camposSenalados = new Set(problemas.map(campoDelProblema).filter(Boolean));
  const timbrable = validacion?.timbrable === true;

  const guardar = async () => {
    const valores = await form.validateFields();
    // Solo se envían los campos que cambiaron
    const cambios = Object.keys(valores).reduce((acumulado, campo) => {
      const anterior = vacioANulo(recibo[campo]);
      const nuevo = vacioANulo(valores[campo]);
      return String(anterior) === String(nuevo) ? acumulado : { ...acumulado, [campo]: nuevo };
    }, {});
    if (!Object.keys(cambios).length) {
      onRevalidar();
      return;
    }
    setGuardando(true);
    try {
      await onGuardar(cambios);
    } finally {
      setGuardando(false);
    }
  };

  const campo = (nombre) => {
    const senalado = camposSenalados.has(nombre);
    const opciones = catalogos?.[nombre];
    return (
      <Form.Item
        key={nombre}
        name={nombre}
        label={ETIQUETAS[nombre]}
        validateStatus={senalado ? 'warning' : undefined}
        help={senalado ? 'Señalado por la validación' : undefined}
      >
        {CAMPOS_CATALOGO.includes(nombre)
          ? (
            <Select
              aria-label={ETIQUETAS[nombre]}
              allowClear
              showSearch
              optionFilterProp="label"
              loading={!catalogos}
              options={opciones || []}
              disabled={soloLectura}
            />
          ) : (
            <Input
              aria-label={ETIQUETAS[nombre]}
              maxLength={LONGITUD_MAXIMA[nombre]}
              disabled={soloLectura}
            />
          )}
      </Form.Item>
    );
  };

  const resultado = () => {
    if (validando) return <Alert type="info" showIcon message="Validando el recibo..." />;
    if (!validacion) {
      return <Alert type="warning" showIcon message="El recibo aún no tiene una validación vigente." />;
    }
    if (timbrable) {
      return <Alert type="success" showIcon message="El recibo pasó la validación y se puede timbrar." />;
    }
    return (
      <List
        size="small"
        bordered
        header={<Text strong>Qué Corregir Antes de Timbrar</Text>}
        dataSource={problemas}
        renderItem={(problema) => {
          const campoProblema = campoDelProblema(problema);
          return (
            <List.Item>
              <Space direction="vertical" size={0}>
                <Text>
                  <Text strong>{problema.donde}</Text>
                  {': '}
                  {problema.mensaje}
                </Text>
                <Text type="secondary" style={{ fontSize: '0.8em' }}>
                  {problema.codigo}
                  {problema.elemento ? ` · ${problema.elemento}` : ''}
                </Text>
                {campoProblema
                  ? <Tag color="orange">{`Corregible: ${ETIQUETAS[campoProblema]}`}</Tag>
                  : <Text type="secondary">{explicacionDelProblema(problema)}</Text>}
              </Space>
            </List.Item>
          );
        }}
      />
    );
  };

  return (
    <Drawer
      title={`Revisar Recibo ${recibo?.numero_y_nombre_completo || ''}`}
      width={560}
      open={visible}
      onClose={onClose}
      destroyOnClose
      footer={(
        <Space wrap>
          <Button
            type="primary"
            onClick={guardar}
            loading={guardando}
            disabled={soloLectura || validando}
          >
            Guardar y Validar
          </Button>
          <Button onClick={onRevalidar} disabled={guardando || validando}>
            Volver a Validar
          </Button>
          <Button
            onClick={onSeleccionar}
            disabled={!timbrable || seleccionado || guardando || validando}
          >
            {seleccionado ? 'Seleccionado para Timbrar' : 'Seleccionar para Timbrar'}
          </Button>
          <Button onClick={onVerDatosTimbrado}>Datos de Timbrado</Button>
        </Space>
      )}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {resultado()}
        {soloLectura && motivoSoloLectura && (
          <Alert type="warning" showIcon message={motivoSoloLectura} />
        )}
        <Alert
          type="info"
          showIcon
          message="Los cambios se guardan en este recibo, no en el catálogo del empleado; corrija también el catálogo para las nóminas siguientes."
        />
        <Form form={form} layout="vertical">
          {CAMPOS_CORREGIBLES_RECIBO.map(campo)}
        </Form>
      </Space>
    </Drawer>
  );
};

CorreccionDatosRecibo.propTypes = {
  visible: PropTypes.bool,
  recibo: PropTypes.shape({
    id: PropTypes.number,
    numero_y_nombre_completo: PropTypes.string,
  }),
  validacion: PropTypes.shape({
    estado: PropTypes.string,
    timbrable: PropTypes.bool,
    problemas: PropTypes.arrayOf(PropTypes.shape({
      codigo: PropTypes.string,
      elemento: PropTypes.string,
      donde: PropTypes.string,
      mensaje: PropTypes.string,
    })),
  }),
  validando: PropTypes.bool,
  soloLectura: PropTypes.bool,
  motivoSoloLectura: PropTypes.string,
  seleccionado: PropTypes.bool,
  onGuardar: PropTypes.func.isRequired,
  onRevalidar: PropTypes.func.isRequired,
  onSeleccionar: PropTypes.func.isRequired,
  onVerDatosTimbrado: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CorreccionDatosRecibo.defaultProps = {
  visible: false,
  recibo: null,
  validacion: null,
  validando: false,
  soloLectura: false,
  motivoSoloLectura: null,
  seleccionado: false,
};

export default CorreccionDatosRecibo;
